import React, { useState } from "react";
import { m as motion } from "../../shared/components/Motion";
import "./Certificates.css";
import Certificate from "./Certificate";
import CertificatesSkeleton from "./CertificatesSkeleton";
import { useCertificatesData } from "../../shared/hooks";
import { useAdmin } from "../../features/admin/AdminProvider";
import EditCertificatesModal from "../../features/admin/modals/EditCertificatesModal";
import {
  containerVariantsSlow as containerVariants,
  childVariantsSlow as childVariants,
} from "../../shared/utils";

const Certificates = () => {
  const [toggleState, setToggleState] = useState(0);
  const { data, loading } = useCertificatesData();
  const { adminMode, isAdmin, certificatesDraft, saveCertificatesDraft } =
    useAdmin();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [isNewCertificate, setIsNewCertificate] = useState(false);

  // Helper: Convert Firestore object representation to array
  const toArray = (data) => {
    if (Array.isArray(data)) return data;
    if (typeof data === "object" && data !== null) {
      return Object.values(data).filter((item) => typeof item === "object");
    }
    return [];
  };

  // Use draft ONLY if admin is authenticated, otherwise use published data
  const displayData =
    isAdmin && certificatesDraft
      ? Array.isArray(certificatesDraft)
        ? certificatesDraft
        : toArray(certificatesDraft)
      : data?.items
        ? toArray(data.items)
        : [];

  const toggleTab = (index) => {
    setToggleState(index);
  };

  const handleEditCertificate = (index) => {
    setEditingIndex(index);
    setIsNewCertificate(false);
    setIsModalOpen(true);
  };

  const handleAddNewCertificate = () => {
    setEditingIndex(null);
    setIsNewCertificate(true);
    setIsModalOpen(true);
  };

  const handleSaveCertificate = (updatedCertificate) => {
    const currentCertificates = displayData || [];
    let newCertificates;

    if (isNewCertificate) {
      // Add new certificate
      newCertificates = [...currentCertificates, updatedCertificate];
    } else {
      // Update existing certificate
      newCertificates = currentCertificates.map((cert, idx) =>
        idx === editingIndex ? updatedCertificate : cert,
      );
    }

    saveCertificatesDraft(newCertificates).catch((error) =>
      console.error("Error saving certificates draft:", error),
    );
    setIsModalOpen(false);
    setEditingIndex(null);
    setIsNewCertificate(false);
  };

  const handleDeleteCertificate = (index) => {
    if (window.confirm("Delete this certificate?")) {
      const newCertificates = displayData.filter((_, i) => i !== index);
      saveCertificatesDraft(newCertificates).catch((error) =>
        console.error("Error saving certificates draft:", error),
      );
    }
  };

  // Show skeleton while loading
  if (loading || !displayData.length) {
    return <CertificatesSkeleton />;
  }

  return (
    <motion.section
      className="certificates section"
      id="certificates"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <motion.h2 className="section__title" variants={childVariants}>
        Certificates
      </motion.h2>
      <motion.span className="section__subtitle" variants={childVariants}>
        My Achievements
      </motion.span>

      <motion.div
        className="certificates__container container grid"
        variants={containerVariants}
      >
        {displayData.map((cert, index) => (
          <motion.div
            key={cert.id || index}
            className="certificates__content"
            initial="hidden"
            animate="visible"
            variants={childVariants}
          >
            <Certificate
              {...cert}
              toggleState={toggleState}
              index={index + 1}
              toggleTab={toggleTab}
            />
            {adminMode && (
              <div
                style={{
                  position: "absolute",
                  top: "0.5rem",
                  right: "0.5rem",
                  display: "flex",
                  gap: "0.25rem",
                  zIndex: 10,
                }}
              >
                <motion.button
                  whileHover={{ scale: 1.15 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => handleEditCertificate(index)}
                  style={{
                    borderRadius: "50%",
                    width: "36px",
                    height: "36px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "var(--body-color)",
                    padding: 0,
                    background: "var(--title-color)",
                  }}
                  title="Edit certificate"
                >
                  <i className="uil uil-edit-alt"></i>
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.15 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => handleDeleteCertificate(index)}
                  style={{
                    borderRadius: "50%",
                    width: "36px",
                    height: "36px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "var(--body-color)",
                    padding: 0,
                    background: "var(--title-color)",
                  }}
                  title="Delete certificate"
                >
                  <i className="bx bx-trash"></i>
                </motion.button>
              </div>
            )}
          </motion.div>
        ))}
        {adminMode && (
          <motion.div
            className="certificates__content admin__certificates__image"
            onClick={handleAddNewCertificate}
            variants={childVariants}
            style={{
              justifyContent: "center",
              height: "13rem",
            }}
          >
            <motion.button
              className="admin__addNew-btn"
              style={{
                width: "45px",
                height: "45px",
                borderRadius: "50%",
                backgroundColor:
                  "color-mix(in srgb, var(--title-color) 5%, var(--body-color))",
                color: "var(--title-color)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "1rem",
              }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              <i
                className="bx bx-plus"
                style={{
                  fontSize: "2rem",
                }}
              ></i>
            </motion.button>
            <span
              className="admin__addNew-text"
              style={{
                fontSize: "1rem",
                fontWeight: "500",
                color: "var(--title-color)",
              }}
            >
              Add New Certificate
            </span>
          </motion.div>
        )}
      </motion.div>

      {/* Edit Modal */}
      <EditCertificatesModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        certificateData={
          editingIndex !== null ? displayData[editingIndex] : null
        }
        onSave={handleSaveCertificate}
        isNewCertificate={isNewCertificate}
      />
    </motion.section>
  );
};

export default Certificates;
